import { z } from 'zod';

/**
 * F36 — the friendly-match proposal→accept handshake between two teams (D4).
 * A proposing-side club trainer/captain proposes; the opponent side accepts or
 * declines. On accept, an F16 booking (friendly_match=true, opponent_team_id)
 * is materialised + an F30 booking chat opens with both sides. F32 open-games
 * stays for the open-spots-marketplace use case; this is the distinct
 * mutual-acceptance flow.
 */

export const FRIENDLY_MATCH_STATUS_VALUES = [
  'proposed',
  'accepted',
  'declined',
  'cancelled',
] as const;
export type FriendlyMatchStatus = (typeof FRIENDLY_MATCH_STATUS_VALUES)[number];

export type FriendlyMatch = {
  readonly id: string;
  readonly proposingTeamId: string;
  readonly opponentTeamId: string;
  readonly proposedByUserId: string;
  readonly subVenueId: string | null;
  readonly locationText: string | null;
  readonly startAt: string;
  readonly endAt: string;
  readonly status: FriendlyMatchStatus;
  readonly decidedByUserId: string | null;
  readonly decidedAt: string | null;
  readonly declineReason: string | null;
  readonly bookingId: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** Enriched row for the friendlies list (both directions). */
export type FriendlyMatchView = FriendlyMatch & {
  readonly proposingTeamName: string;
  readonly opponentTeamName: string;
};

/** POST /me/club/teams/:teamId/friendlies — propose a friendly. */
export const ProposeFriendlyMatchInputSchema = z
  .object({
    opponentTeamId: z.string().uuid(),
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    subVenueId: z.string().uuid().nullable().optional(),
    locationText: z.string().min(1).max(200).nullable().optional(),
  })
  .strict()
  .refine((v) => Boolean(v.subVenueId) || Boolean(v.locationText), {
    message: 'a friendly needs a place: set subVenueId or locationText',
    path: ['locationText'],
  });
export type ProposeFriendlyMatchInput = z.infer<typeof ProposeFriendlyMatchInputSchema>;

/** POST /me/club/friendlies/:id/decline */
export const DeclineFriendlyMatchInputSchema = z
  .object({ reason: z.string().min(1).max(500).optional() })
  .strict();
export type DeclineFriendlyMatchInput = z.infer<typeof DeclineFriendlyMatchInputSchema>;
